import { useMiddleware } from "@/store/middleware";
import { useRegister } from "@/store/register";
import { checkSubdomainExist, isSpecialSubdomain } from "~/utils/helper";

export default defineNuxtRouteMiddleware(async (to, from) => {
  const auth = useAuth();
  const { $reportError } = useNuxtApp();
  const register = useRegister();
  // Check is session then return to allow participant middleware to control

  if (to.query?.trial_period_token) {
    register.trial.trial_period_token = to.query?.trial_period_token as string;
  }

  // Deliberately above the early returns below: the whole `/register/*` funnel
  // exits at `to.meta.register`, and that is exactly the traffic arriving from an
  // ad. Only used when the attribution cookie is missing.
  captureAdAttribution(to);

  if (to.meta.session) return;
  if (to.meta.register) return;
  if (to.meta.maintenance) return;

  const middleware = useMiddleware();
  const config = useRuntimeConfig();

  if (config.public.maintenanceMode) {
    return navigateTo(`${config.public.primaryURL}/system/maintenance`, {
      external: true,
    });
  }

  if (to.params?.user_id) {
    const pathSplit = to.fullPath.split("/");
    auth.model_type = pathSplit[3];
  }

  //Check is subdomain exists
  if (!isSpecialSubdomain(auth?.subdomain)) {
    try {
      await checkSubdomainExist(middleware, auth?.subdomain);
    } catch (e: any) {
      if (!e.response || (e.response.status != 404 && e.response.status != 401)) {
        $reportError(e);
      }
      return navigateTo(`${config.public.primaryURL}/login`, {
        external: true,
      });
    }
  }

  //Default route no index
  if (to.path === "/") {
    return navigateTo("/login");
  }

  //Check if route is public
  if (to.meta.noAuth) return;

  //  Check if user is logged in if not refetch user cookie
  if (!auth.isSuccess) {
    try {
      await auth.refetch();
    } catch (e) {
      return navigateTo("/login");
    }
  }

  //Check id user has access to that account if not redirect to switch only if not a special subdomain
  const account = ref<{ account_status: string }>({ account_status: "" });
  if (!isSpecialSubdomain(auth?.subdomain)) {
    try {
      if (!middleware.account || middleware.accountDataIsStale) {
        await middleware.accountRefetch();
      }
      account.value = middleware.account;
    } catch (e) {
      if (auth.isSuccess) {
        return navigateTo(`${config.public.primaryURL}/switch`, {
          external: true,
        });
      } else {
        return navigateTo(`${config.public.primaryURL}/login`, {
          external: true,
        });
      }
    }
  }

  //If user is logged in check account status and redirect to correct page
  if (auth.isSuccess) {
    if (to.meta.onboarding) return;
    checkStatus(account.value?.account_status);
  } else {
    return navigateTo("/login");
  }

  function checkStatus(status: string) {
    switch (status) {
      case "ONBOARDING":
        return navigateTo(`${config.public.primaryURL}/onboarding/account`, {
          external: true,
        });
      case "ACTIVE":
        return;
      case "PENDING":
        return navigateTo(`${config.public.primaryURL}/system/pending`, {
          external: true,
        });
      case "CLOSED":
        return navigateTo(`${config.public.primaryURL}/system/closed`, {
          external: true,
        });
      default:
        return;
    }
  }
});
